import { useEffect, useMemo, useState } from "react";

import { SectionShell } from "./SectionShell";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ToastType } from "@/enums";
import { useWorkspaces } from "@/queries";
import {
  IdentitySettings,
  useUpdateIdentitySettings,
} from "@/queries/identity/useIdentitySettings";
import { useWorkspaceRoles } from "@/queries/identity/useWorkspaceRoles";
import type { Workspace } from "@/types/workspaces";
import { displayToast } from "@/utils/toast";

interface Props {
  organizationId: number;
  settings: IdentitySettings;
  onError: (msg: string | null) => void;
}

/**
 * Default workspace picker for JIT-provisioned users when no IdP
 * group mapping applies. The role list is scoped to the selected
 * workspace; selections outside the current org are filtered out.
 */
export const DefaultWorkspaceSection = ({ organizationId, settings, onError }: Props) => {
  const { data: workspacesData } = useWorkspaces();
  const updateSettings = useUpdateIdentitySettings(organizationId);
  const [selectedId, setSelectedId] = useState<number | null>(settings.default_workspace_id);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(
    settings.default_workspace_role_id,
  );

  const orgWorkspaces = useMemo<Workspace[]>(() => {
    const list = workspacesData?.workspaces;
    if (!list) {
      return [];
    }
    return list.filter(w => w.organization_id === organizationId);
  }, [workspacesData, organizationId]);

  // Roles are scoped to the selected workspace so we never persist a
  // role id that does not exist there.
  const { data: rolesData } = useWorkspaceRoles(selectedId);
  const roles = rolesData?.roles ?? [];

  useEffect(() => {
    if (selectedRoleId === null) {
      return;
    }
    if (!rolesData) {
      return;
    }
    const isStillValid = rolesData.roles.some(r => r.id === selectedRoleId);
    if (!isStillValid) {
      setSelectedRoleId(null);
    }
  }, [rolesData, selectedRoleId]);

  const handleSave = () => {
    onError(null);
    updateSettings.mutate(
      {
        default_workspace_id: selectedId,
        default_workspace_role_id: selectedId !== null ? selectedRoleId : null,
      },
      {
        onSuccess: () => displayToast("Default workspace saved.", ToastType.success),
        onError: () => onError("Failed to save default workspace."),
      },
    );
  };

  const isDirty =
    selectedId !== settings.default_workspace_id ||
    selectedRoleId !== settings.default_workspace_role_id;

  return (
    <SectionShell
      title="Default workspace"
      description="Where new SSO users land if no IdP-group mapping applies."
    >
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-700">Workspace</label>
          <Select
            value={selectedId !== null ? String(selectedId) : "none"}
            onValueChange={value => setSelectedId(value === "none" ? null : Number(value))}
          >
            <SelectTrigger className="h-9 w-full">
              <SelectValue placeholder="Select a workspace" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No default (require group mapping)</SelectItem>
              {orgWorkspaces.map(w => (
                <SelectItem key={w.id} value={String(w.id)}>
                  {w.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-700">Default role</label>
          <Select
            value={selectedRoleId !== null ? String(selectedRoleId) : "none"}
            onValueChange={value => setSelectedRoleId(value === "none" ? null : Number(value))}
            disabled={selectedId === null || roles.length === 0}
          >
            <SelectTrigger className="h-9 w-full min-w-[200px]">
              <SelectValue
                placeholder={
                  selectedId === null ? "Pick a workspace first" : "Use workspace default"
                }
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Use workspace default</SelectItem>
              {roles.map(r => (
                <SelectItem key={r.id} value={String(r.id)}>
                  {r.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button onClick={handleSave} disabled={updateSettings.isLoading || !isDirty}>
          Save
        </Button>
      </div>
    </SectionShell>
  );
};
